import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  ListItemText,
  Tooltip,
  IconButton,
} from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import PropTypes from 'prop-types';

import { CommonDialog, CheckBox } from 'components';
import { Context } from 'context';
import { ADD_NOTIFICATION } from 'context/actions';
import {
  SSO_SERVICES,
  USER_SERVICES,
  PARTNER_SERVICES,
  GROUP_SERVICES,
  REGION_SERVICES,
  LOCATION_SERVICES,
} from 'Services';
import Utils from 'Shared/Utils';
import { PERMISSIONS_LABEL } from 'Views/Common/enum';

import { GROUP_IDENTIFIER_LABEL, IDENTIFIER_TYPE, getGroupKeyField } from './constants';

const ACCESS_SCOPES = [
  { value: 'full', label: 'Full' },
  { value: 'clinic_based', label: 'Clinic Based' },
];

// Roles allowed per SSO config level — prevents assigning roles
// above the SSO configuration scope (e.g. partner roles on group-level SSO).
const ALLOWED_ROLE_PREFIXES = {
  partner: ['partner', 'group', 'region', 'location'],
  group: ['group', 'region', 'location'],
  region: ['region', 'location'],
  location: ['location'],
};

const LEVEL_ENTITY_CONFIG = {
  partner: { idKey: 'partner_id', nameKey: 'partner_name', label: 'Partner' },
  group: { idKey: 'group_id', nameKey: 'group_name', label: 'Group' },
  region: { idKey: 'region_id', nameKey: 'region_name', label: 'Region' },
  location: { idKey: 'location_id', nameKey: 'location_name', label: 'Location' },
};

const getLevelFromRole = (role) => {
  if (!role) return '';
  if (role.startsWith('partner')) return 'partner';
  if (role.startsWith('group')) return 'group';
  if (role.startsWith('region')) return 'region';
  if (role.startsWith('location')) return 'location';
  return '';
};

const getInitialFormState = (mappingData, identifierType) => ({
  idp_group_id: mappingData?.idp_group_id || '',
  idp_group_name: mappingData?.idp_group_name || '',
  internal_role: mappingData?.internal_role || '',
  access_scope: identifierType === IDENTIFIER_TYPE.NAME
    ? (mappingData?.access_scope || 'full')
    : 'full',
  level_ids: mappingData?.level_ids || [],
  features: mappingData?.features || [],
});

const GroupMappingFormModal = ({
  level,
  levelId,
  partnerId,
  groupId,
  ssoProvider,
  identifierType = IDENTIFIER_TYPE.ID,
  mappingData,
  onClose,
  onSave,
}) => {
  const { dispatch, state: { user = {} } } = useContext(Context);
  const isEditMode = Boolean(mappingData);
  const isInternalUser = Utils.checkIfInternalUser(user);

  const [formState, setFormState] = useState(getInitialFormState(mappingData, identifierType));
  const [actionLoading, setActionLoading] = useState(false);
  const [isFormTouched, setIsFormTouched] = useState(false);

  const [rolesList, setRolesList] = useState({});
  const [rolesLoading, setRolesLoading] = useState(true);
  const [allFeatures, setAllFeatures] = useState([]);
  const [featuresList, setFeaturesList] = useState([]);
  const [featuresLoading, setFeaturesLoading] = useState(true);
  const [entityList, setEntityList] = useState([]);
  const [entityLoading, setEntityLoading] = useState(false);

  const derivedLevel = getLevelFromRole(formState.internal_role);
  const entityConfig = LEVEL_ENTITY_CONFIG[derivedLevel];
  const isLocationRole = derivedLevel === 'location';
  const canUseClinicBased = isLocationRole && identifierType === IDENTIFIER_TYPE.NAME;

  // The identifier type decides which field is the matching key. Email is a
  // Google-only type and is stored in idp_group_id.
  const keyField = getGroupKeyField(identifierType);
  const isKeyField = (field) => field === keyField;
  const isEmailIdentifier = identifierType === IDENTIFIER_TYPE.EMAIL;

  const groupIdIsKey = isKeyField('idp_group_id');
  const groupIdLabelBase =
    GROUP_IDENTIFIER_LABEL[isEmailIdentifier ? IDENTIFIER_TYPE.EMAIL : IDENTIFIER_TYPE.ID];
  const groupIdLabel = groupIdIsKey ? `${groupIdLabelBase} *` : groupIdLabelBase;

  // The Admin SDK returns group emails lowercased and jit_service matches
  // them verbatim, so a mixed-case entry saves cleanly and then matches
  // nothing at login. Normalise before it reaches the API.
  const normalizeGroupId = (value) => {
    const trimmed = (value || '').trim();
    return isEmailIdentifier ? trimmed.toLowerCase() : trimmed;
  };

  // Same acceptance as a single-@ "local@domain.tld" pattern, checked with
  // index arithmetic so there is no regex backtracking to worry about.
  const canEditGroupEmail = isEmailIdentifier && !(isEditMode && groupIdIsKey);

  const isValidGroupEmail = (value) => {
    if (!value || /\s/.test(value)) return false;
    const at = value.indexOf('@');
    if (at < 1 || at !== value.lastIndexOf('@')) return false;
    const domain = value.slice(at + 1);
    const dot = domain.indexOf('.');
    return dot > 0 && dot < domain.length - 1;
  };

  const getGroupIdHelperText = () => {
    if (isEditMode && groupIdIsKey) {
      const subject = isEmailIdentifier ? 'Group email' : 'Group ID';
      return `${subject} cannot be changed (matching key)`;
    }
    if (isEmailIdentifier) {
      return 'The Google Workspace group email address';
    }
    return 'The unique identifier from your Identity Provider';
  };

  const entityMap = useMemo(() => {
    if (!entityConfig) return new Map();
    const map = new Map();
    entityList.forEach((e) => map.set(e[entityConfig.idKey], e));
    return map;
  }, [entityList, entityConfig]);

  useEffect(() => {
    fetchRoles();
    fetchFeatures();
  }, []);

  useEffect(() => {
    if (derivedLevel) {
      fetchEntityList(derivedLevel);
    } else {
      setEntityList([]);
    }

    if (formState.internal_role && allFeatures.length && isInternalUser) {
      const filtered = allFeatures.filter((f) =>
        f.validate_access?.includes(formState.internal_role),
      );
      setFeaturesList(filtered);

      const validKeys = new Set(filtered.map((f) => f.key));
      const currentFeatures = formState.features.filter((f) => validKeys.has(f));
      if (currentFeatures.length !== formState.features.length) {
        setFormState((prev) => ({ ...prev, features: currentFeatures }));
      }
    } else {
      setFeaturesList([]);
    }
  }, [formState.internal_role, allFeatures, isInternalUser]);

  const fetchRoles = async () => {
    try {
      const response = await USER_SERVICES.getRoles();
      if (response?.type === 'success') {
        setRolesList(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    }
    setRolesLoading(false);
  };

  const fetchFeatures = async () => {
    try {
      const response = await USER_SERVICES.getUserFeatures();
      if (response?.type === 'success' && response?.data) {
        setAllFeatures(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch features:', error);
    }
    setFeaturesLoading(false);
  };

  const fetchEntityList = async (entityLevel) => {
    setEntityLoading(true);
    try {
      let response;
      switch (entityLevel) {
        case 'partner':
          response = await PARTNER_SERVICES.fetchAllPartners();
          break;
        case 'group':
          response = await GROUP_SERVICES.fetchGroupByPartnerId({ partner_id: partnerId });
          break;
        case 'region':
          response = await REGION_SERVICES.fetchRegionByQueryParams(partnerId, groupId);
          break;
        case 'location':
          response = await LOCATION_SERVICES.fetchLocationByPartnerId(groupId, partnerId);
          break;
        default:
          setEntityList([]);
          setEntityLoading(false);
          return;
      }
      if (response?.type === 'success') {
        setEntityList(response?.data?.rows || []);
      }
    } catch (error) {
      console.error('Failed to fetch entity list:', error);
      setEntityList([]);
    }
    setEntityLoading(false);
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;

    if (field === 'internal_role') {
      const canClinic = getLevelFromRole(value) === 'location' && identifierType === IDENTIFIER_TYPE.NAME;
      setFormState((prev) => ({
        ...prev,
        internal_role: value,
        level_ids: [],
        features: [],
        access_scope: canClinic ? prev.access_scope : 'full',
      }));
    } else {
      setFormState((prev) => ({ ...prev, [field]: value }));
    }
    setIsFormTouched(true);
  };

  const setNotification = (severity, message) => {
    dispatch({
      type: ADD_NOTIFICATION,
      payload: { notification: { severity, message } },
    });
  };

  const isFormValid = () => {
    const { idp_group_id, idp_group_name, internal_role, level_ids } = formState;
    const hasRequiredGroupField = Boolean(
      keyField === 'idp_group_name' ? idp_group_name : idp_group_id
    );
    // Only enforced where the field can actually be edited. In edit mode
    // the key field is disabled, so a mapping keyed before the config
    // switched to email matching would otherwise be unsaveable.
    if (canEditGroupEmail && !isValidGroupEmail(normalizeGroupId(idp_group_id))) {
      return false;
    }
    return Boolean(hasRequiredGroupField && internal_role && level_ids.length > 0);
  };

  const buildPayload = () => {
    const { idp_group_id, idp_group_name, internal_role, access_scope, level_ids, features } =
      formState;

    const payload = {
      internal_role,
      access_scope,
    };

    if (isEditMode) {
      // On update, include the editable (non-key) group field if non-empty
      if (!isKeyField('idp_group_name') && idp_group_name) {
        payload.idp_group_name = idp_group_name.trim();
      }
      if (!isKeyField('idp_group_id') && idp_group_id) {
        payload.idp_group_id = normalizeGroupId(idp_group_id);
      }
    } else {
      // On create, include both fields if non-empty
      if (idp_group_id) payload.idp_group_id = normalizeGroupId(idp_group_id);
      if (idp_group_name) payload.idp_group_name = idp_group_name.trim();
    }

    payload.level_ids = level_ids;

    if (isInternalUser) {
      payload.features = features.length > 0 ? features : [];
    }

    return payload;
  };

  const handleSave = async () => {
    setActionLoading(true);
    try {
      const payload = buildPayload();
      let response;

      if (isEditMode) {
        response = await SSO_SERVICES.updateGroupMapping(mappingData.id, payload);
      } else {
        response = await SSO_SERVICES.createGroupMapping(level, levelId, ssoProvider, payload);
      }

      if (Utils.checkIfSuccess(response)) {
        setNotification(
          'success',
          `Group mapping ${isEditMode ? 'updated' : 'created'} successfully`,
        );
        onSave();
      } else {
        setNotification('error', response?.message || 'Failed to save group mapping');
      }
    } catch (error) {
      console.error('Error saving group mapping:', error);
      setNotification('error', 'An error occurred while saving group mapping');
    }
    setActionLoading(false);
  };

  const renderEntityName = (selected) => {
    if (!selected || selected.length === 0 || !entityConfig) return '';
    return selected
      .map((id) => {
        const entity = entityMap.get(id);
        return entity?.[entityConfig.nameKey] || id;
      })
      .filter(Boolean)
      .join(', ');
  };

  return (
    <CommonDialog
      open={true}
      handleClose={onClose}
      dialogTitle={isEditMode ? 'Edit Group Mapping' : 'Add Group Mapping'}
      primaryBtnTxt="Save"
      handlePrimaryButtonClick={handleSave}
      disabledPrimaryButton={!isFormTouched || !isFormValid() || actionLoading}
      primaryButtonStyles={{ minWidth: 105 }}
      isLoading={actionLoading}
      secondaryBtnTxt="Cancel"
      disableSecondaryButton={actionLoading}
      maxWidth="sm"
    >
      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12}>
          <TextField
            fullWidth
            variant="outlined"
            label={groupIdLabel}
            value={formState.idp_group_id}
            onChange={handleChange('idp_group_id')}
            disabled={isEditMode && groupIdIsKey}
            placeholder={isEmailIdentifier ? 'group@company.com' : ''}
            error={
              canEditGroupEmail &&
              Boolean(formState.idp_group_id) &&
              !isValidGroupEmail(normalizeGroupId(formState.idp_group_id))
            }
            helperText={getGroupIdHelperText()}
            data-testid="mapping-idp-group-id"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            variant="outlined"
            label={`IdP Group Name${isKeyField('idp_group_name') ? ' *' : ''}`}
            value={formState.idp_group_name}
            onChange={handleChange('idp_group_name')}
            disabled={isEditMode && isKeyField('idp_group_name')}
            helperText={
              isEditMode && isKeyField('idp_group_name')
                ? 'Group name cannot be changed (matching key)'
                : 'Display name for this IdP group'
            }
            data-testid="mapping-idp-group-name"
          />
        </Grid>

        <Grid item xs={12} sm={6} className="position-relative">
          <FormControl fullWidth variant="outlined" disabled={rolesLoading}>
            <InputLabel>Internal Role *</InputLabel>
            <Select
              value={formState.internal_role}
              onChange={handleChange('internal_role')}
              label="Internal Role *"
              data-testid="mapping-internal-role"
            >
              {Object.keys(rolesList)
                .filter((roleKey) => {
                  if (roleKey === 'superadmin' || roleKey === 'subscribili') return false;
                  const allowedPrefixes = ALLOWED_ROLE_PREFIXES[level] || [];
                  return allowedPrefixes.some((prefix) => roleKey.startsWith(prefix));
                })
                .map((roleKey) => (
                  <MenuItem key={roleKey} value={roleKey}>
                    {rolesList[roleKey]}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          {rolesLoading && (
            <CircularProgress size={20} thickness={5} className="dropdown-loader" />
          )}
        </Grid>

        <Grid item xs={12} sm={6} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <FormControl fullWidth variant="outlined" disabled={!canUseClinicBased}>
            <InputLabel>Access Scope</InputLabel>
            <Select
              value={formState.access_scope}
              onChange={handleChange('access_scope')}
              label="Access Scope"
              data-testid="mapping-access-scope"
            >
              {ACCESS_SCOPES.map((scope) => (
                <MenuItem key={scope.value} value={scope.value}>
                  {scope.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip
            title="Clinic Based is only available when the SSO config matches groups by name and for location-level roles. Location is resolved from the IdP group name (e.g. '1011 - users'), falling back to the selected entities. Group emails do not qualify: '1011-admins@company.com' would parse as clinic 1011 by accident."
            arrow
          >
            <IconButton size="small">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Grid>

        {derivedLevel && entityConfig && (
          <Grid item xs={12} className="position-relative">
            <FormControl fullWidth variant="outlined" disabled={entityLoading}>
              <InputLabel>{entityConfig.label}(s) *</InputLabel>
              <Select
                multiple
                value={formState.level_ids}
                onChange={handleChange('level_ids')}
                label={`${entityConfig.label}(s) *`}
                data-testid="mapping-level-ids"
                renderValue={renderEntityName}
              >
                {entityList.map((entity) => (
                  <MenuItem
                    key={entity[entityConfig.idKey]}
                    value={entity[entityConfig.idKey]}
                  >
                    <CheckBox
                      checked={formState.level_ids.includes(entity[entityConfig.idKey])}
                    />
                    <ListItemText primary={entity[entityConfig.nameKey]} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {entityLoading && (
              <CircularProgress size={20} thickness={5} className="dropdown-loader" />
            )}
          </Grid>
        )}

        {Boolean(featuresList.length) && (
          <Grid item xs={12} className="position-relative">
            <FormControl fullWidth variant="outlined" disabled={featuresLoading}>
              <InputLabel>Features</InputLabel>
              <Select
                multiple
                value={formState.features}
                onChange={handleChange('features')}
                label="Features"
                data-testid="mapping-features"
                renderValue={(selected) =>
                  selected.map((key) => PERMISSIONS_LABEL[key] || key).join(', ')
                }
              >
                {featuresList.map((feature) => (
                  <MenuItem key={feature.key} value={feature.key}>
                    <CheckBox checked={formState.features.includes(feature.key)} />
                    <ListItemText
                      primary={PERMISSIONS_LABEL[feature.key] || feature.name}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {featuresLoading && (
              <CircularProgress size={20} thickness={5} className="dropdown-loader" />
            )}
          </Grid>
        )}
      </Grid>
    </CommonDialog>
  );
};

GroupMappingFormModal.propTypes = {
  level: PropTypes.string.isRequired,
  levelId: PropTypes.string.isRequired,
  partnerId: PropTypes.string,
  groupId: PropTypes.string,
  ssoProvider: PropTypes.string.isRequired,
  identifierType: PropTypes.string,
  mappingData: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default GroupMappingFormModal;
